// src/api/shareLinks.js
//
// API helpers for the share_links table.
// Share links are hashed UUID tokens that grant read-only public access
// to a project or doc without requiring authentication.
//
// URL scheme:
//   /share/p/:token  → public project view
//   /share/d/:token  → public doc view

import { supabase } from '../supabaseClient';
import { sanitizeInputByType, sanitizeStructuredInput } from '../helpers/formatHelpers';

const LINK_TYPE_PROJECT = 'project';
const LINK_TYPE_DOC = 'doc';

// ── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Returns true if a share link row is currently active (enabled and not expired).
 */
function isLinkActive(link) {
  if (!link?.enabled) return false;
  if (link.expires_at && new Date(link.expires_at) < new Date()) return false;
  return true;
}

/**
 * Build the public share URL for a resolved link row.
 */
export function buildShareUrl(link) {
  if (!link?.id || !link?.link_type) return null;
  const type = link.link_type === LINK_TYPE_PROJECT ? 'p' : 'd';
  return `${window.location.origin}/share/${type}/${link.id}`;
}

// ── CRUD ─────────────────────────────────────────────────────────────────────

/**
 * Create a share link for a project or doc.
 *
 * @param {'project'|'doc'} linkType
 * @param {number} targetId  – project_id or doc_id
 * @param {string} userId    – auth user id (created_by)
 * @param {object|null} visualState – optional saved view state JSON
 * @returns {{ link: object, shareUrl: string }}
 */
export async function apiCreateShareLink(linkType, targetId, userId, visualState = null) {
  const safeLinkType = sanitizeInputByType(linkType, 'identifier', { maxLength: 20 });
  const safeUserId = sanitizeInputByType(userId, 'identifier', { maxLength: 120 });
  const { data, error } = await supabase
    .from('share_links')
    .insert({
      link_type: safeLinkType === LINK_TYPE_PROJECT ? LINK_TYPE_PROJECT : LINK_TYPE_DOC,
      target_id: targetId,
      created_by: safeUserId,
      visual_state: sanitizeStructuredInput(visualState ?? null, { maxDepth: 8, maxStringLength: 2000 }),
      enabled: true,
    })
    .select()
    .single();

  if (error) throw new Error(error.message);

  return {
    link: data,
    shareUrl: buildShareUrl(data),
  };
}

/**
 * Resolve a share token (UUID).
 * Always returns the raw row (or null if not found).
 * Callers should use isLinkActive() / validateShareLink() to check active state.
 *
 * @param {string} token – UUID from the URL
 * @returns {object|null}
 */
export async function apiGetShareLink(token) {
  const safeToken = sanitizeInputByType(token, 'identifier', { maxLength: 120 });
  if (!safeToken) return null;

  const { data, error } = await supabase
    .from('share_links')
    .select('*')
    .eq('id', safeToken)
    .maybeSingle();

  if (error || !data) return null;

  return data;
}

/**
 * Toggle enabled state of a share link.
 * @param {string} token
 * @param {boolean} enabled
 * @returns {object} updated row
 */
export async function apiToggleShareLink(token, enabled) {
  const safeToken = sanitizeInputByType(token, 'identifier', { maxLength: 120 });
  const { data, error } = await supabase
    .from('share_links')
    .update({ enabled: Boolean(enabled) })
    .eq('id', safeToken)
    .select()
    .single();

  if (error) throw new Error(error.message);
  return { ...data, shareUrl: buildShareUrl(data), isActive: isLinkActive(data) };
}

/**
 * Hard-delete a share link. The token becomes permanently invalid.
 *
 * @param {string} token – UUID
 */
export async function apiRevokeShareLink(token) {
  const safeToken = sanitizeInputByType(token, 'identifier', { maxLength: 120 });
  const { error } = await supabase
    .from('share_links')
    .delete()
    .eq('id', safeToken);

  if (error) throw new Error(error.message);
}

/**
 * Update the visual_state on an existing share link.
 * Visual state encodes which tabs are open, active pane, split-screen flag.
 *
 * Shape: { openTabs: [{ docId, pane }], activePane, splitScreen }
 *
 * @param {string} token
 * @param {object} visualState
 */
export async function apiUpdateShareLinkVisualState(token, visualState) {
  const safeToken = sanitizeInputByType(token, 'identifier', { maxLength: 120 });
  const { error } = await supabase
    .from('share_links')
    .update({ visual_state: sanitizeStructuredInput(visualState, { maxDepth: 8, maxStringLength: 2000 }) })
    .eq('id', safeToken);

  if (error) throw new Error(error.message);
}

/**
 * Set an expiry date on a share link (null = never expires).
 *
 * @param {string} token
 * @param {Date|string|null} expiresAt
 */
export async function apiSetShareLinkExpiry(token, expiresAt) {
  const safeToken = sanitizeInputByType(token, 'identifier', { maxLength: 120 });
  const { error } = await supabase
    .from('share_links')
    .update({ expires_at: expiresAt ? new Date(expiresAt).toISOString() : null })
    .eq('id', safeToken);

  if (error) throw new Error(error.message);
}

/**
 * List all active share links owned by the current user for a given target.
 *
 * @param {'project'|'doc'} linkType
 * @param {number} targetId
 * @returns {object[]}
 */
export async function apiGetShareLinksForTarget(linkType, targetId) {
  const safeLinkType = sanitizeInputByType(linkType, 'identifier', { maxLength: 20 });
  const { data, error } = await supabase
    .from('share_links')
    .select('*')
    .eq('link_type', safeLinkType === LINK_TYPE_PROJECT ? LINK_TYPE_PROJECT : LINK_TYPE_DOC)
    .eq('target_id', targetId)
    .order('created_at', { ascending: false });

  if (error) throw new Error(error.message);

  return (data || []).map(link => ({
    ...link,
    shareUrl: buildShareUrl(link),
    isActive: isLinkActive(link),
  }));
}

/**
 * Client-side validation helper for a share link row.
 * @param {object|null} shareLink
 * @returns {{ valid: boolean, reason?: string }}
 */
export function validateShareLink(shareLink) {
  if (!shareLink) return { valid: false, reason: 'not_found' };
  if (!shareLink.enabled) return { valid: false, reason: 'disabled' };
  if (shareLink.expires_at) {
    const expiry = new Date(shareLink.expires_at);
    if (Number.isNaN(expiry.getTime()) || expiry < new Date()) {
      return { valid: false, reason: 'expired' };
    }
  }
  return { valid: true };
}

export { LINK_TYPE_PROJECT, LINK_TYPE_DOC, isLinkActive };
